/**
 * Firm (B2B) onboarding WRAPPER store — Zustand + persist.
 *
 * The fresh-firm onboarding (`/onboarding/firm`) is a guided wrapper that takes
 * a brand-new practice from "email verified" to a set-up admin console. Like the
 * individual (B2C) wrapper, it owns the OUTER gates BEFORE the optional setup
 * steps run:
 *
 *   1. `provisioned` — the firm tenant + firm-admin have been minted (onboard
 *                      POST, accountType 'firm') + the session refreshed so the
 *                      JWT carries the new tenant_id + role 'admin'. Until this
 *                      is true every /v1/admin/* call would 403 (no tenant on
 *                      the claims).
 *   2. `mfaDone`     — a TOTP authenticator was enrolled + verified inline. The
 *                      brand-publish + client-create steps that follow are
 *                      step-up gated server-side, so the firm reaches aal2 here
 *                      (and the MFA step stamps step-up freshness on verify).
 *
 * After both gates, the wrapper runs a sequence of SKIPPABLE setup steps the
 * firm can also finish later from the console:
 *
 *   brand → company → billing → (done → /admin/dashboard)
 *
 * That progression is a simple cursor (`setupStep`) rather than a per-step
 * boolean, because every step is optional and strictly sequential — `advanceSetup`
 * walks to the next, `goToSetupStep` jumps, and the terminal `done` value tells
 * the wrapper to leave for the dashboard.
 *
 * ── 2026-08-23 (Paul, on the team walkthrough) ────────────────────────────────
 *
 * `welcome` LEFT the cursor, and nothing replaced it. Paul: "skip this
 * completely — the full registration process should be as quick as possible."
 * The orientation screen (five role cards) and the standalone "before you start"
 * overview were BOTH removed; the CIPC upload that lived on the second of those
 * two screens became a step in its own right (`company`) and absorbed the
 * greeting into its own heading. The overview copy did not die — it is what the
 * upload's loading state now says while the document is being read, which is the
 * only moment a firm has any reason to read it.
 *
 * ── 2026-08-22 (Paul's ruling — DASHBOARD FIRST) ──────────────────────────────
 *
 * `clients` LEFT the cursor. It was step 3 of 4 and it stood between a fresh firm
 * and its own console: a practice could not see the product it had just signed up
 * for until it had been walked through loading its book. The ruling is that
 * nothing about a firm's CLIENTS may be forced before the dashboard — the Client
 * Book is reachable from the console, which is where a firm manages its book from
 * anyway.
 *
 * `FirmClientsStep` is UNCHANGED in what it does; it now renders in a disclosure
 * panel in the wrapper chrome (like the team invites before it), available from
 * every setup step and compulsory on none. Same move, same reason.
 *
 * BILLING STAYED as the one step after brand, on purpose. Paul's 2026-08-18
 * ruling puts the payment decision immediately BEFORE the first document upload,
 * because ingestion is the step that costs real money. Ingestion starts from the
 * console (Client Book → Map structure → the builder), so the last honest place to
 * ask that still precedes it is the final onboarding step. Moving it to a
 * dismissible dashboard prompt was the alternative and it loses the guarantee: a
 * dismissed prompt would let a firm reach the builder having never chosen, and
 * `TenantDoc.openSignup.billingOwner` would go unwritten — which the individual
 * wrapper reads to replace a joining client's paid step. Two clicks before the
 * dashboard is the cheaper honest version.
 *
 * ── 2026-08-18 (Paul's ruling off Beno's TrustCo flowchart) ───────────────────
 *
 * The cursor changed from `brand → team` to `welcome → brand → clients → billing`:
 *   - `welcome`  — greeting + overview, and where CIPC company documents went.
 *                  RETIRED from the cursor 2026-08-23 (above); the CIPC upload
 *                  it carried is now the `company` step.
 *   - `brand`    — unchanged.
 *   - `clients`  — the guided first run. RETIRED from the cursor 2026-08-22 (above).
 *   - `billing`  — WHO CARRIES THE BILL. Writes the EXISTING seam,
 *                  `TenantDoc.openSignup.billingOwner` (PATCH /v1/admin/branding),
 *                  which `self-join` already stamps onto each joining client and
 *                  the individual wrapper already reads to skip its paid step.
 *   - `team`     — GONE from the cursor. It was step 4 of 4 and blocking; it is
 *                  now a side action available from every setup step, so a firm
 *                  can invite staff alongside the rest instead of after it.
 *
 * Persisted under a versioned key so a refresh mid-onboarding resumes at the
 * right place (e.g. provisioned-but-not-MFA'd lands back on the MFA step rather
 * than re-minting a tenant). `clear()` resets it (called on exit / restart).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/** The optional, sequential setup steps that follow the two hard gates.
 *
 *  `company` is the CIPC step (2026-08-23): the firm's own registration papers,
 *  and the screen that greets the practice by name. */
export type FirmSetupStep = 'brand' | 'company' | 'billing';

/** Steps that USED to be in the cursor and can still arrive from a browser that
 *  started onboarding under an older order. They are never rendered as steps —
 *  they are mapped forward (see `resolveSetupCursor`) so a firm mid-onboarding
 *  resumes at a real step instead of crashing or dead-ending on a blank card.
 *
 *   - `welcome` — a cursor step 2026-08-18 → 2026-08-23.
 *   - `clients` — a cursor step 2026-08-18 → 2026-08-22.
 *   - `team`    — a cursor step until 2026-08-18. */
export type FirmRetiredSetupStep = 'welcome' | 'clients' | 'team';

/** The cursor value, including the terminal `done` state the wrapper exits on. */
export type FirmSetupCursor = FirmSetupStep | 'done';

/** Step order — the single source of truth the wrapper + `advanceSetup` share. */
export const FIRM_SETUP_STEP_ORDER: FirmSetupStep[] = ['brand', 'company', 'billing'];

/** Where a retired cursor resumes.
 *
 *   - `welcome` sat BEFORE brand, so it resumes at `brand` — the first step this
 *     build renders. A firm that was staring at the role cards when the flow
 *     changed picks up at branding rather than being skipped past it.
 *   - `clients` / `team` both sat AFTER brand, so they pick up at the first step
 *     that still follows brand rather than being pushed out to the dashboard
 *     with the CIPC and billing questions never asked. */
const RETIRED_STEP_FORWARD: Record<FirmRetiredSetupStep, FirmSetupCursor> = {
  welcome: 'brand',
  clients: 'company',
  team: 'company',
};

/** WHO CARRIES THE BILL for the firm's clients. Mirrors
 *  `TenantDoc.openSignup.billingOwner` exactly — 'firm' means the practice pays
 *  and a joining client's paid step is replaced by a calm confirm panel;
 *  'client' means each client pays for themselves. `null` = not chosen yet. */
export type FirmBillingOwner = 'firm' | 'client' | null;

export interface FirmOnboardingStore {
  /** The firm tenant + firm-admin have been minted + the session refreshed. */
  provisioned: boolean;
  /** A TOTP authenticator was enrolled + verified (→ aal2 + step-up fresh). */
  mfaDone: boolean;
  /** Where in the optional setup sequence we are (or `done` → exit). */
  setupStep: FirmSetupCursor;
  /** The billing-owner choice saved on the Payment step. Kept locally so the
   *  step can reflect the firm's own choice on a resume — GET /v1/admin/branding
   *  returns the branding overlay only and does NOT echo `openSignup` back
   *  (checked 2026-08-18, `admin-branding-get.ts`), so there is nothing to
   *  re-read it from. The authoritative copy is the tenant doc the PATCH wrote. */
  billingOwner: FirmBillingOwner;

  setProvisioned: (v: boolean) => void;
  setMfaDone: (v: boolean) => void;
  setBillingOwner: (v: FirmBillingOwner) => void;
  /** Walk the cursor to the next setup step; past the last → `done`. */
  advanceSetup: () => void;
  /** Jump the cursor directly to a named step (or `done`). */
  goToSetupStep: (step: FirmSetupCursor) => void;
  /** Reset all wrapper state (called on exit / restart). */
  clear: () => void;
}

/** What actually goes to localStorage. The action functions are not persisted
 *  (JSON drops them anyway) — spelling the shape out keeps `migrate` honest. */
interface FirmOnboardingPersisted {
  provisioned: boolean;
  mfaDone: boolean;
  setupStep: FirmSetupCursor;
  billingOwner: FirmBillingOwner;
}

// The KEY is deliberately still `:v2` while the persist VERSION is 4. Renaming
// the key would orphan every blob written before 2026-08-22 — a firm halfway
// through onboarding would silently restart from the provisioning gate. The
// version bump + `migrate` below is what handles the shape change; the key only
// has to stay findable.
const STORAGE_KEY = 'elreign:firm-onboarding:v2';

/** The cursor's initial value — the first optional step. */
const FIRST_SETUP_STEP: FirmSetupCursor = 'brand';

/** Map ANY cursor value — current, retired, or garbage off a hand-edited blob —
 *  onto a step this build can actually render. Pure; unit-tested.
 *
 *  This is the backwards-compatibility seam: `'clients'` was a real cursor value
 *  between 2026-08-18 and 2026-08-22 and is still sitting in the localStorage of
 *  every firm that started onboarding in those four days. */
export function resolveSetupCursor(value: unknown): FirmSetupCursor {
  if (value === 'done') return 'done';
  if (typeof value === 'string') {
    if ((FIRM_SETUP_STEP_ORDER as string[]).includes(value)) return value as FirmSetupStep;
    const forward = RETIRED_STEP_FORWARD[value as FirmRetiredSetupStep];
    if (forward) return forward;
  }
  return FIRST_SETUP_STEP;
}

/** Next cursor value after `current`; past the last step returns `done`. A
 *  retired cursor advances from wherever it RESOLVES to (which is what the firm
 *  is looking at), and a cursor already at `done` stays `done`. */
function nextCursor(current: FirmSetupCursor): FirmSetupCursor {
  const resolved = resolveSetupCursor(current);
  const idx = FIRM_SETUP_STEP_ORDER.indexOf(resolved as FirmSetupStep);
  if (idx === -1) return 'done';
  const next = FIRM_SETUP_STEP_ORDER[idx + 1];
  return next ?? 'done';
}

export const useFirmOnboardingStore = create<FirmOnboardingStore>()(
  persist(
    (set) => ({
      provisioned: false,
      mfaDone: false,
      setupStep: FIRST_SETUP_STEP,
      billingOwner: null,

      setProvisioned: (provisioned) => set({ provisioned }),
      setMfaDone: (mfaDone) => set({ mfaDone }),
      setBillingOwner: (billingOwner) => set({ billingOwner }),
      advanceSetup: () => set((s) => ({ setupStep: nextCursor(s.setupStep) })),
      // Normalised on the way in too, so nothing can park the cursor on a step
      // this build has no case for.
      goToSetupStep: (setupStep) => set({ setupStep: resolveSetupCursor(setupStep) }),

      clear: () =>
        set({
          provisioned: false,
          mfaDone: false,
          setupStep: FIRST_SETUP_STEP,
          billingOwner: null,
        }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: 4,
      partialize: (s): FirmOnboardingPersisted => ({
        provisioned: s.provisioned,
        mfaDone: s.mfaDone,
        setupStep: s.setupStep,
        billingOwner: s.billingOwner,
      }),
      // v4 (2026-08-23): `welcome` left the cursor too — same treatment.
      // v3 (2026-08-22): `clients` left the cursor. Rehydrating it as-is would
      // strand a mid-onboarding firm on a step with no case in the wrapper's
      // switch, so the blob is read field by field and the cursor mapped forward.
      // Both gates are kept: they are expensive to redo (a re-provision would try
      // to mint a second tenant) and both re-derive themselves on mount anyway.
      migrate: (persisted): FirmOnboardingPersisted => {
        const s = (persisted ?? {}) as Record<string, unknown>;
        const owner = s['billingOwner'];
        return {
          provisioned: s['provisioned'] === true,
          mfaDone: s['mfaDone'] === true,
          setupStep: resolveSetupCursor(s['setupStep']),
          billingOwner: owner === 'firm' || owner === 'client' ? owner : null,
        };
      },
    },
  ),
);
